import React, { FC, memo, ReactNode } from 'react';
import { useMutation } from 'react-query';
import { UsersAPI } from 'api/UsersAPI';
import { ExclamationCircleOutlined } from '@ant-design/icons/lib';
import { Button, message, Modal, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { WarningOutlined } from '@ant-design/icons';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface UsersActivateButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  userId: number;
  userName: string;
  tooltip?: ReactNode;
  onStatusChange?: () => void;
  actionType: 'activate' | 'deactivate';
}

export const UsersActivateButton: FC<UsersActivateButtonProps> = memo(
  ({
    actionType,
    userId,
    userName,
    onStatusChange,
    tooltip,
    ...buttonProps
  }) => {
    const [mutate, { isLoading }] =
      actionType === 'activate'
        ? useMutation(UsersAPI.activateUser)
        : useMutation(UsersAPI.deactivateUser);
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title: intlMessage(
          {
            id:
              actionType === 'activate'
                ? 'businessPartners.activate.confirmation'
                : 'businessPartners.deactivate.confirmation',
          },
          {
            name: userName,
          }
        ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'primary',
        onOk: () =>
          mutate(userId, {
            onSuccess: () => {
              message.success(
                intlMessage(
                  {
                    id:
                      actionType === 'activate'
                        ? 'user.activated'
                        : 'user.deactivated',
                  },
                  {
                    name: userName,
                  }
                )
              );
              onStatusChange && onStatusChange();
            },
          }),
      });

    const button = (
      <Button
        id="button-activate-user"
        loading={isLoading}
        onClick={() => confirm()}
        icon={<WarningOutlined />}
        {...buttonProps}
      />
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
  }
);
